import React from "react";
import { useUI } from "../../stores/useUI";
import { useLevelProgress } from "../../stores/useLevelProgress";

const GlobeIcon = () => (
  <svg width="100%" height="100%" viewBox="-0.5 -0.5 25 25" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block' }}>
    {/* <circle cx="12" cy="12" r="12" stroke="#521616" strokeWidth="0.8"/> */}
    <ellipse cx="12" cy="12" rx="5.5" ry="12.5" stroke="#521616" strokeWidth="0.8"/>
    <line x1="0" y1="8" x2="24" y2="8" stroke="#521616" strokeWidth="0.8"/>
    <line x1="0" y1="16" x2="24" y2="16" stroke="#521616" strokeWidth="0.8"/>
  </svg>
);

const PILL: React.CSSProperties = {
  background: '#FBF2D3',
  border: '1px solid #521616',
  borderRadius: '93px',
  boxShadow: 'inset -3px -3px 0px rgba(212, 28, 28, 0.25)',
  color: '#521616',
  fontFamily: '"IBM Plex Mono", monospace',
  fontSize: '20px',
  fontWeight: 400,
  lineHeight: '26px',
  height: '42px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
};

export default function MainHeader({ handleBack }: { handleBack: () => void }) {
  const { currentMap, setStatsPopupIsOpen, statsPopupIsOpen } = useUI();
  const { getMaps, getMapData, isNPCCompleted, unlockedMaps, getWorldConfig } = useLevelProgress();
  const worldConfig = getWorldConfig();
  const mapName = worldConfig?.maps.find(m => m.id === currentMap)?.name;

  const handleBackClick = () => {
    if (statsPopupIsOpen) {
      setStatsPopupIsOpen(false);
    } else if (currentMap) {
      handleBack();
    }
  };

  const handleStatsClick = () => {
    setStatsPopupIsOpen(true);
  };

  let progressText = "";
  if (!currentMap) {
    progressText = `${unlockedMaps.size} / ${getMaps().length}`;
  } else {
    const mapData = getMapData()[currentMap];
    if (mapData) {
      const completed = mapData.npcs.filter(npc => isNPCCompleted(currentMap, npc.id)).length;
      progressText = `${completed} / ${mapData.npcs.length}`;
    }
  }
  const showBackButton = currentMap || statsPopupIsOpen;

  const infoButton = (
    <button style={{ ...PILL, width: '42px', padding: 0, cursor: 'default' }}>i</button>
  );

  return (
    <div data-main-header style={styles.wrapper}>
      {/* Left: back + counter (+ info when popup is open) */}
      <div style={styles.leftGroup}>
        {showBackButton && (
          <button onClick={handleBackClick} style={{ ...PILL, width: '42px', padding: 0}}>
            {currentMap && !statsPopupIsOpen ? <GlobeIcon /> : '←'}
          </button>
        )}
        <button onClick={handleStatsClick} style={{ ...PILL, padding: '6px 28px' }}>
          {progressText}
        </button>
        {statsPopupIsOpen && infoButton}
      </div>

      {/* Title */}
      <div style={statsPopupIsOpen ? styles.titleRight : styles.center}>
        <span style={statsPopupIsOpen ? styles.titleTextOpen : styles.titleText}>
          {currentMap ? mapName : worldConfig?.worldName}
        </span>
      </div>

      {/* Right: info (only when popup is closed) */}
      {!statsPopupIsOpen && (
        <div style={styles.rightGroup}>
          {infoButton}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrapper: {
    position: 'fixed',
    top: '20px',
    left: '32px',
    right: '32px',
    zIndex: 1001,
    display: 'flex',
    alignItems: 'center',
  },
  leftGroup: {
    flex: '1 1 0px' as any,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  center: {
    flex: '4 1 0px' as any,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    // maxWidth: '70%',
    overflow: 'hidden',
  },
  titleRight: {
    flex: '4 1 0px' as any,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    pointerEvents: 'none',
    maxWidth: '70%',
    overflow: 'hidden',
  },
  rightGroup: {
    flex: '1 1 0px' as any,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  titleText: {
    fontFamily: '"Sixtyfour", monospace',
    fontSize: 'clamp(32px, 3.5vw, 52px)',
    color: '#843B37',
    lineHeight: 1,
    letterSpacing: '1px',
    textAlign: 'center',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
    overflow: 'hidden',
    minWidth: 0,
  } as React.CSSProperties,
  titleTextOpen: {
    fontFamily: '"Sixtyfour", monospace',
    fontSize: 'clamp(40px, 4.7vw, 68px)',
    color: '#FBF2D3',
    lineHeight: 1,
    letterSpacing: '1px',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 3,
    overflow: 'hidden',
    minWidth: 0,
    textAlign: 'right'
  } as React.CSSProperties,
};
